import React, { useRef, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { Box, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import BasicTabs from '../../common/basic-tabs/BasicTabs';
import DetailsTabs from './components/DetailsTab';
import ContactsTabs from './components/ContactsTabs';
import DocumentsTabs from './components/DocumentsTabs';
import NotesTab from '../../common/notes-tab/NotesTab';
import FormUpload from '../../common/form-upload/FormUpload';
import UploadButton from '../../common/upload-button/UploadButton';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import {
  selectBusinessProfileContacts,
  selectBusinessProfileDetails,
} from '../../slices/business-profile/businessProfileSlice';
import styles from './BusinessProfile.module.scss';
import CreateContactModal from '../../common/create-contact-modal/CreateContactModal';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';

import Button from '@mui/material/Button';
import { useDocuments } from '../document-vault/hooks/useDocuments';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { Document } from '../document-vault/components/document-list-vault/types';
import { useProfileDocumentsQuery } from '../document-vault/hooks/useProfileDocumentsQuery';
import { DOCUMENTS_PER_PAGE } from '../document-vault/hooks/vaultQueryParams';
import { RootState } from '../../store';
import { ROUTES } from '../../common/constants/routes';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';

const BusinessProfile = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploadAndSignOpen, setIsUploadAndSignOpen] = useState(false);
  const documentsLoading = useAppSelector((state: RootState) => state.documents.status);
  const company = useAppSelector(selectAllCompanies)[0];
  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  // Profile tab, selected workflow, card/list view and page live in the URL query,
  // so the browser Back button from a document details page restores the same view.
  const {
    tab: activeTab,
    setTab: setActiveTab,
    workflow,
    setWorkflow,
  } = useProfileDocumentsQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sortByDate = (a: any, b: any) => {
    return Number(new Date(b.created)) - Number(new Date(a.created));
  };
  const {
    documents,
    documentsCount,
    documentsPage,
    getRequestedPage,
    showWorkflowBreadcrumbs,
    setShowWorkflowBreadcrumbs,
    cardView,
    setCardView,
  } = useDocuments('business', workflow ?? undefined);
  const combinedDocuments = [...documents].slice().sort((a, b) => sortByDate(a, b));

  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  const handleUploadOpen = () => {
    setIsUploadOpen((prev) => !prev);
  };

  const toggleShowAddModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning(cardView);
  const {
    patchDocumentWithProfilePerson,
    setAssignee,
    assignee /*checkProfilePersonsAvailability*/,
  } = useAssignProfilePersonModal(documentToSign?.category, documentToSign);

  const handleUploadAndSignClose = () => {
    setIsUploadAndSignOpen(false);
  };

  const handleUploadAndSignOpen = () => {
    setIsUploadAndSignOpen(true);
  };

  const handleUploadAndSignSuccess = (uploadedDocument: DocumentVaultItemModel, tab?: Window) => {
    const document = uploadedDocument as Document;
    /*if (
      (document.customers?.length ?? 0) === 0 &&
      (document.employees?.length ?? 0) === 0 &&
      (document.suppliers?.length ?? 0) === 0 &&
      (document.business_profile_contacts?.length ?? 0) === 0 &&
      checkProfilePersonsAvailability(document.category ?? '')
    ) {
      dispatch(hideOverlay());
      if (tab) {
        tab.close();
      }
      setDocumentToSign(document);
      setOpenAssignPersonDialog(true);
    } else {*/
    handleSignOriginalDocument(document, undefined, undefined, tab);
    //}
  };

  // Items per page - adjust this value according to your API
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / DOCUMENTS_PER_PAGE);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    // Written to the URL (?page=N); the documents are fetched from it
    getRequestedPage(page);
  };

  if (!company) {
    return <CircularProgress />;
  }

  return (
    <>
      <AppHeader
        title={
          <>
            <span style={{ marginRight: '8px' }}>{t('headers.businessProfile')}</span>
            <PreviewTooltip title={<Preview />} arrow placement='top'>
              <InfoIcon fontSize='small' className={'info-icon'} />
            </PreviewTooltip>
          </>
        }
      />
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid className={'controls-grid'} component='div' size={{ xs: 12 }}>
          <Stack component='div' spacing={0} sx={{ marginBottom: '20px' }}>
            <Stack
              component='div'
              direction='row'
              spacing={1}
              useFlexGap
              sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
                display: { xs: 'block', md: 'flex' },
              }}
            >
              <Stack
                sx={{
                  alignItems: 'center',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <Box ref={uploadButtonRef} style={{ position: 'relative' }}>
                  <UploadButton
                    id='businessProfileUploadFilesBtn'
                    onClick={handleUploadOpen}
                    buttonText={t('buttons.uploadFiles')}
                    isArrowDown={false}
                  />
                  {isUploadOpen && (
                    <Box
                      className={styles.form_box}
                      ref={formUploadRef}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      <FormUpload
                        businessContacts={businessContacts ?? undefined}
                        onCancel={handleUploadClose}
                        categoryValue='business'
                        workflow={workflow}
                      />
                    </Box>
                  )}
                </Box>

                <Box style={{ position: 'relative' }}>
                  <UploadButton
                    id='businessProfileUploadAndSignPdfBtn'
                    onClick={handleUploadAndSignOpen}
                    buttonText={t('buttons.uploadAndSignPDF')}
                    isArrowDown={false}
                    icon={EditNoteOutlinedIcon}
                  />
                  {isUploadAndSignOpen && (
                    <Box className={styles.form_box} style={{ alignSelf: 'flex-start' }}>
                      <FormUpload
                        businessContacts={businessContacts ?? undefined}
                        onCancel={handleUploadAndSignClose}
                        categoryValue='business'
                        workflow={workflow}
                        uploadWithSigning={true}
                        accept='.pdf'
                        supportedFormatsKey='messages.supportedFormatsPDF'
                        maxFiles={1}
                        onUploadSuccess={handleUploadAndSignSuccess}
                      />
                    </Box>
                  )}
                </Box>
                <Tooltip
                  title={t('buttons.createContact')}
                  arrow
                  placement='top'
                  /*sx={{ marginRight: 'auto' }}*/
                >
                  <Button
                    id='businessProfileCreateContactBtn'
                    onClick={toggleShowAddModal}
                    className={styles.add_button}
                  >
                    <ContactMailOutlinedIcon id='businessProfileCreateContactBtnIcon' />
                    <span id='businessProfileCreateContactBtnSpan'>
                      {t('buttons.createContact')}
                    </span>
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
          <Box className={styles.document_header}>
            <Typography variant='h6' component='h2'>
              {businessProfileDetails && company ? company.name : ''}
            </Typography>
          </Box>
        </Grid>
        <Grid component='div' size={{ xs: 12 }} className={styles.grid_margin_bottom}>
          <BasicTabs
            tabs={[
              /*t('businessProfile.allTab'),*/
              t('businessProfile.documentsTabs'),
              t('businessProfile.detailsTab'),
              t('businessProfile.contactsTab'),
              t('businessProfile.notesTab'),
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabContents={[
              /*<AllTabs company={company} documents={combinedDocuments} key='all' />,*/
              <DocumentsTabs
                company={company}
                documents={combinedDocuments}
                key='documents'
                cardView={cardView}
                workflow={workflow}
                setWorkflow={setWorkflow}
                showWorkflowBreadcrumbs={showWorkflowBreadcrumbs}
                setShowWorkflowBreadcrumbs={setShowWorkflowBreadcrumbs}
                documentsActions={{
                  idPrefix: 'businessProfile',
                  cardView,
                  onCardView: () => setCardView(true),
                  onListView: () => setCardView(false),
                  listViewDisabled: !!workflow && showWorkflowBreadcrumbs,
                  showPagination: documents.length > 0 && (!!workflow || !cardView),
                  page: documentsPage,
                  totalPages,
                  onPageChange: handlePageChange,
                  paginationDisabled: documentsLoading === 'loading',
                }}
              />,
              <DetailsTabs company={company} key='details' />,
              <ContactsTabs companyId={company.id as number} key='contacts' />,
              <NotesTab companyId={company?.id} key='notes' />,
            ]}
          />
        </Grid>
        {isModalOpen && (
          <CreateContactModal
            companyId={company.id as number}
            open={isModalOpen}
            onClose={() => toggleShowAddModal()}
          />
        )}
        {openAssignPersonDialog && documentToSign && (
          <AssignProfilePersonModal
            open={openAssignPersonDialog}
            onClose={() => {
              setOpenAssignPersonDialog(false);
              setDocumentToSign(null as unknown as Document);
              setAssignee(null as unknown as string);
            }}
            modalTitle={t('headers.startSigning')}
            category={documentToSign?.category ?? ''}
            document={documentToSign}
            goBackButtonText={t('buttons.goBack')}
            modalMessage={t('messages.signingRecommendations')}
            assignee={assignee}
            setAssignee={setAssignee}
            signOriginalDocument={true}
            secondaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee) {
                // @ts-expect-error: there can be an error
                handleSignOriginalDocument(documentToSign, Number(assignee), undefined, tab);
              } else {
                // @ts-expect-error: there can be an error
                handleSignOriginalDocument(documentToSign, undefined, true, tab);
              }
            }}
            primaryAction={() => {
              dispatch(showOverlay());
              const url = `${window.location.origin}${ROUTES.PROCESSING}`;
              const tab = window.open('', '_blank');
              // @ts-expect-error: there can be an error
              tab.location.href = url;
              if (assignee && assignee !== 'noAssignee') {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => handleSignLatestVersion(res, undefined, undefined, undefined, tab))
                  .catch(() =>
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab)
                  );
              } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                handleSignLatestVersion(documentToSign, undefined, undefined, undefined, tab);
              }
            }}
          />
        )}
      </Grid>
    </>
  );
};

export default BusinessProfile;
